import { createI18n } from 'vue-i18n';
import enLocale from './locales/en.js';
import zhCNLocale from './locales/zh-CN.js';
import zhTWLocale from './locales/zh-TW.js';
import zhHKLocale from './locales/zh-HK.js';
import jaLocale from './locales/ja.js';
import koLocale from './locales/ko.js';
import frLocale from './locales/fr.js';

// 可用的语言列表和对应的语言包
const languageResources = {
  'en': enLocale,
  'zh-CN': zhCNLocale,
  'zh-TW': zhTWLocale,
  'zh-HK': zhHKLocale,
  'ja': jaLocale,
  'ko': koLocale,
  'fr': frLocale
};

// 可用的语言列表
const availableLanguages = Object.keys(languageResources);

// 语言显示名称
export const languageNames = {
  'en': 'English',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文 (台灣)',
  'zh-HK': '繁體中文 (香港)',
  'ja': '日本語',
  'ko': '한국어',
  'fr': 'Français'
};

// 语言图标（国旗）
export const languageFlags = {
  'en': '🇺🇸',
  'zh-CN': '🇨🇳',
  'zh-TW': '🇹🇼',
  'zh-HK': '🇭🇰',
  'ja': '🇯🇵',
  'ko': '🇰🇷',
  'fr': '🇫🇷'
};

// 获取浏览器首选语言或从本地存储中获取
const getBrowserLanguage = () => {
  // 1. 首先检查本地存储中是否有语言设置
  const savedLanguage = localStorage.getItem('language');
  if (savedLanguage && availableLanguages.includes(savedLanguage)) {
    console.log(`Using saved language from localStorage: ${savedLanguage}`);
    return savedLanguage;
  }
  
  // 2. 获取浏览器首选语言
  const browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
  console.log(`Detected browser language: ${browserLang}`);
  
  // 3. 检查完整的语言代码匹配
  if (availableLanguages.includes(browserLang)) {
    console.log(`Browser language ${browserLang} is supported, using it`);
    return browserLang;
  }
  
  // 4. 简化的语言代码匹配
  const simpleLang = browserLang.split('-')[0];
  if (availableLanguages.includes(simpleLang)) {
    console.log(`Simplified browser language ${simpleLang} is supported, using it`);
    return simpleLang;
  }
  
  // 5. 处理中文特殊情况
  if (browserLang.startsWith('zh')) {
    // 区分简体和繁体中文
    // zh-CN, zh-SG -> 简体中文
    // zh-TW -> 繁体中文(台湾)
    // zh-HK -> 繁体中文(香港)
    if (browserLang.includes('hk')) {
      console.log(`Chinese variant detected (Hong Kong): ${browserLang}, using zh-HK`);
      return 'zh-HK';
    } else if (browserLang.includes('tw')) {
      console.log(`Chinese variant detected (Taiwan): ${browserLang}, using zh-TW`);
      return 'zh-TW';
    } else {
      console.log(`Chinese variant detected (Simplified): ${browserLang}, using zh-CN`);
      return 'zh-CN';
    }
  }
  
  // 6. 默认返回英语
  console.log(`Browser language ${browserLang} is not supported, using default (en)`);
  return 'en';
};

const i18n = createI18n({
  legacy: false, // 使用组合式API
  locale: getBrowserLanguage(),
  fallbackLocale: 'en',
  messages: languageResources,
  warnHtmlInMessage: 'off',
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false
});

// 更改语言的工具函数
export async function setLanguage(lang) {
  if (!languageResources[lang]) {
    console.error(`Language ${lang} not supported`);
    return;
  }

  try {
    i18n.global.locale.value = lang;
    localStorage.setItem('language', lang);
    document.querySelector('html').setAttribute('lang', lang);
    console.log(`Language changed successfully to: ${lang}`);
  } catch (error) {
    console.error('Error changing language:', error);
  }
}

// 获取当前语言
export const getCurrentLanguage = () => {
  return i18n.global.locale.value;
};

export default i18n; 